import { Injectable } from "@angular/core";
import { HttpClient, HttpParams } from "@angular/common/http";
import { Observable } from "rxjs";
import { environment } from "src/environments/environment";
import { Endpoint } from "src/app/shared/endpoints";

export type ContentActivityType = "guide" | "guidePack";

export interface ContentFieldChange {
  field?: string;
  before?: string | null;
  after?: string | null;
}

export interface ContentActivityEntry {
  id?: string;
  contentId?: string;
  contentType?: string;
  contentTitle?: string;
  organizationId?: string;
  actorEmail?: string;
  actorName?: string;
  actorProfilePicUrl?: string;
  action?: string;
  category?: "Approval" | "Edit" | "Access" | string;
  fromState?: string;
  toState?: string;
  comment?: string;
  version?: number;
  changes?: ContentFieldChange[];
  createdAtUtc?: string;
}

export interface OrgAuditEntry extends ContentActivityEntry {
  targetType?: string;
  targetId?: string;
  targetTitle?: string;
  detailsJson?: string;
}

export interface ContentApprovalStatus {
  contentId?: string;
  contentType?: string;
  title?: string;
  approvalState?: string;
  approverEmail?: string;
  changeSummary?: string;
  organizationId?: string;
  applicationId?: string;
  teamId?: string;
  ownerEmail?: string;
  isLive?: boolean;
  /** null until the creator has chosen; true keeps the published version visible during edits. */
  keepPublishedLive?: boolean | null;
  hasPendingDraft?: boolean;
  pendingDraftUtc?: string;
  pendingDraftBy?: string;
  version?: number;
  publishedVersion?: number;
  canRestorePublished?: boolean;
  latestRejection?: ApprovalAuditEntry | null;
  audit?: ApprovalAuditEntry[];
}

export interface ApprovalAuditEntry {
  action?: string;
  actorEmail?: string;
  comment?: string;
  atUtc?: string;
  fromState?: string;
  toState?: string;
}

@Injectable({ providedIn: "root" })
export class ContentActivityService {
  private readonly base = environment.apiBaseUrl;

  constructor(private http: HttpClient) {}

  list(
    contentType: ContentActivityType,
    contentId: string,
    take = 200
  ): Observable<ContentActivityEntry[]> {
    const params = new HttpParams()
      .set("contentType", contentType)
      .set("contentId", contentId)
      .set("take", String(take));
    return this.http.get<ContentActivityEntry[]>(
      `${this.base}${Endpoint.contentActivityUrl}`,
      { params }
    );
  }

  approvalStatus(
    contentType: ContentActivityType,
    contentId: string
  ): Observable<ContentApprovalStatus> {
    const params = new HttpParams()
      .set("contentType", contentType)
      .set("contentId", contentId);
    return this.http.get<ContentApprovalStatus>(
      `${this.base}${Endpoint.contentApprovalStatusUrl}`,
      { params }
    );
  }

  orgAuditLog(
    organizationId: string,
    targetType?: string,
    take = 200
  ): Observable<OrgAuditEntry[]> {
    let params = new HttpParams().set("take", String(take));
    if (targetType) params = params.set("targetType", targetType);
    return this.http.get<OrgAuditEntry[]>(
      `${this.base}${Endpoint.organizationByIdUrl}/${encodeURIComponent(
        organizationId
      )}/audit`,
      { params }
    );
  }
}
